import { useMemo } from 'react';
import { signupValidationMessages } from '@/Pages/Signup/copy';
import { validateBirthDate } from '@/Pages/Signup/utils/age';
import { DEFAULT_NATIONALITY } from '@/Pages/Signup/utils/countries';
import { validateCpf } from '@/Pages/Signup/utils/cpfValidation';
import { normalizeSignupFormData } from '@/Pages/Signup/utils/normalizeSignupForm';
import type { SignupFormData } from '@/types/signup';

const MIN_FULL_NAME_LENGTH = 2;
const MIN_INTERNATIONAL_DOCUMENT_LENGTH = 6;
const BRAZIL_NATIONALITY = 'BR';
const INTERNATIONAL_DOCUMENT_PATTERN = /^[A-Za-z0-9\- ]+$/;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export { signupValidationMessages };

export function validateSignupField(
  field: keyof SignupFormData,
  form: SignupFormData,
): string | undefined {
  const normalized = normalizeSignupFormData(form);

  if (field === 'fullName') {
    if (normalized.fullName.length < MIN_FULL_NAME_LENGTH) {
      return signupValidationMessages.fullName;
    }
    return undefined;
  }

  if (field === 'email') {
    if (!EMAIL_PATTERN.test(normalized.email)) {
      return signupValidationMessages.email;
    }
    return undefined;
  }

  if (field === 'birthDate') {
    const result = validateBirthDate(normalized.birthDate);
    if (!result.valid) {
      return result.message;
    }
    return undefined;
  }

  if (field === 'nationality') {
    if (!normalized.nationality) {
      return signupValidationMessages.nationality;
    }
    return undefined;
  }

  if (field === 'document') {
    if (normalized.nationality === BRAZIL_NATIONALITY) {
      const cpfResult = validateCpf(form.document);
      if (!cpfResult.valid) {
        return cpfResult.message;
      }
      return undefined;
    }
    if (normalized.document.length < MIN_INTERNATIONAL_DOCUMENT_LENGTH) {
      return signupValidationMessages.documentMinLength;
    }
    if (!INTERNATIONAL_DOCUMENT_PATTERN.test(normalized.document)) {
      return signupValidationMessages.documentFormat;
    }
    return undefined;
  }

  return undefined;
}

export function isSignupFormValid(form: SignupFormData): boolean {
  const fields: (keyof SignupFormData)[] = [
    'fullName',
    'email',
    'birthDate',
    'nationality',
    'document',
  ];
  return fields.every((field) => validateSignupField(field, form) === undefined);
}

export type UseSignupValidationResult = {
  validateField: (field: keyof SignupFormData, form: SignupFormData) => string | undefined;
  isFormValid: (form: SignupFormData) => boolean;
};

export function useSignupValidation(): UseSignupValidationResult {
  return useMemo(
    () => ({
      validateField: validateSignupField,
      isFormValid: isSignupFormValid,
    }),
    [],
  );
}

export function createInitialSignupForm(): SignupFormData {
  return {
    fullName: '',
    email: '',
    birthDate: '',
    nationality: DEFAULT_NATIONALITY,
    document: '',
  };
}
